import { useCallback, useMemo } from 'react';
import { useDebouncedValue, useFormField } from '@frontend/design-system';

export interface FilterableCheckboxListItem {
  value: string;
  label: string;
  isArchived?: boolean;
  locationName?: string;
}

const filterBySearchTerm = <T extends FilterableCheckboxListItem>(list?: T[], searchTerm?: string): T[] | undefined => {
  if (!searchTerm?.trim()) return list;

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  return list?.filter((item) => item.label.toLowerCase().includes(normalizedSearchTerm));
};

interface UseCheckListFilterParams<T extends FilterableCheckboxListItem = FilterableCheckboxListItem> {
  list?: T[];
  onChange: (selectedValues: string[]) => void;
  selectedValues: string[];
}

/**
 * Custom hook that provides filtering, searching, and selection management
 * for a list of checkboxes with bulk operations support.
 *
 * Features:
 * - Real-time search filtering with debouncing
 * - Select all/clear all functionality (respects current filter)
 * - Optimized performance with memoization
 * - Type-safe interface
 *
 * @param params - Configuration object for the hook
 * @returns Object containing filtered data, handlers, and state flags
 *
 */
export const useFilterableCheckboxList = <T extends FilterableCheckboxListItem = FilterableCheckboxListItem>({
  list,
  selectedValues,
  onChange,
}: UseCheckListFilterParams<T>) => {
  const searchField = useFormField({ type: 'text' });

  const debouncedSearchTerm = useDebouncedValue(searchField.value);

  const handleSelectAll = useCallback(() => {
    const filteredItems = filterBySearchTerm<T>(list, debouncedSearchTerm);
    if (!filteredItems?.length) return;

    // Get values that aren't already selected
    const newValues = filteredItems.map((item) => item.value).filter((value) => !selectedValues.includes(value));

    if (newValues.length > 0) {
      // Note: need to pass only new values to avoid unchecking already selected items
      onChange(newValues);
    }
  }, [list, debouncedSearchTerm, selectedValues, onChange]);

  const handleClear = useCallback(() => {
    const filteredItems = filterBySearchTerm<T>(list, debouncedSearchTerm);
    if (!filteredItems?.length) return;

    // get filtered & selected items to remove those from selection
    const valuesToRemove = filteredItems
      .filter((item) => selectedValues.includes(item.value))
      .map((item) => item.value);

    onChange(valuesToRemove);
  }, [list, debouncedSearchTerm, selectedValues, onChange]);

  const filteredList = useMemo(() => filterBySearchTerm<T>(list, debouncedSearchTerm), [list, debouncedSearchTerm]);

  const isAllSelected = useMemo(() => {
    if (!filteredList?.length) return false;
    return filteredList.every((item) => selectedValues.includes(item.value));
  }, [selectedValues, filteredList]);

  const isAnySelected = useMemo(() => {
    if (!filteredList?.length) return false;
    return filteredList.some((item) => selectedValues.includes(item.value));
  }, [selectedValues, filteredList]);

  return {
    // Search functionality
    searchField,

    // Data
    filteredList,

    // Selection state
    isAllSelected,
    isAnySelected,

    // Actions
    handleSelectAll,
    handleClear,
  };
};
